import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { GoogleLogin } from "@react-oauth/google";
import api from "../api/axios";
import OTPVerify from "./OTPVerify";

const HERO_WORDS = [
  { fa: "سلام", en: "Hello" },
  { fa: "کتاب", en: "Book" },
  { fa: "خانه", en: "House" },
  { fa: "دوست", en: "Friend" },
  { fa: "زیبا", en: "Beautiful" },
  { fa: "روز",  en: "Day" },
];

function TulipMark({ size = 34 }) {
  const r = Math.round(size * 0.28);
  return (
    <div style={{ width: size, height: size, borderRadius: r, background: "#1a8f68", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <svg width={size * 0.72} height={size * 0.72} viewBox="0 0 22 26" fill="none">
        <line x1="11" y1="25" x2="11" y2="16" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
        <path d="M11 20 Q7 18 6.5 14 Q9 15.5 11 19" fill="white" opacity="0.7"/>
        <path d="M11 16 Q6 12 6 7.5 Q8.5 5.5 11 9 Q13.5 5.5 16 7.5 Q16 12 11 16Z" fill="white"/>
        <path d="M11 15 Q9 12 9.5 8.5 Q10.5 11 11 14 Q11.5 11 12.5 8.5 Q13 12 11 15Z" fill="#1a8f68" opacity="0.4"/>
      </svg>
    </div>
  );
}

export default function Landing() {
  const { loginWithToken } = useAuth();
  const toast = useToast();
  const isMobile = useIsMobile();
  const [cardMode, setCardMode] = useState("login"); // "login" | "signup" | "otp"
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({ name: "", email: "", password: "" });
  const [otpEmail, setOtpEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [wordIdx, setWordIdx] = useState(0);
  const [wordVisible, setWordVisible] = useState(true);
  const [navShadow, setNavShadow] = useState(false);
  const scWordRef = useRef(null);
  const scTransRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => {
      setWordVisible(false);
      setTimeout(() => {
        setWordIdx(i => {
          const next = (i + 1) % HERO_WORDS.length;
          if (scWordRef.current) {
            scWordRef.current.style.opacity = "0";
            scWordRef.current.style.transform = "translateY(10px)";
          }
          if (scTransRef.current) scTransRef.current.style.opacity = "0";
          setTimeout(() => {
            if (scWordRef.current) {
              scWordRef.current.textContent = HERO_WORDS[next].fa;
              scWordRef.current.style.transition = "opacity .5s,transform .5s";
              scWordRef.current.style.opacity = "1";
              scWordRef.current.style.transform = "translateY(0)";
            }
            if (scTransRef.current) {
              scTransRef.current.textContent = HERO_WORDS[next].en;
              scTransRef.current.style.transition = "opacity .5s";
              scTransRef.current.style.opacity = "1";
            }
          }, 380);
          return next;
        });
        setWordVisible(true);
      }, 380);
    }, 3200);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const h = () => setNavShadow(window.scrollY > 50);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll(".lp-reveal");
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add("lp-visible"); obs.unobserve(e.target); } });
    }, { threshold: 0.1 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const switchToSignup = () => {
    setCardMode("signup");
    setErrors({});
    setTimeout(() => cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };

  const switchToLogin = () => {
    setCardMode("login");
    setErrors({});
  };

  const handleLogin = async () => {
    const e = {};
    if (!loginForm.email) e.email = "Email is required";
    if (!loginForm.password) e.password = "Password is required";
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true); setErrors({});
    try {
      const res = await api.post("/auth/login", { email: loginForm.email.trim().toLowerCase(), password: loginForm.password });
      loginWithToken(res.data.token, res.data.user);
      toast.success(`Welcome back, ${res.data.user.name}!`);
    } catch (err) {
      const msg = err.response?.data?.message || "Login failed.";
      toast.error(msg); setErrors({ general: msg });
    } finally { setLoading(false); }
  };

  const handleSignup = async () => {
    const e = {};
    if (!signupForm.name.trim()) e.name = "Name is required";
    if (!signupForm.email.trim()) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(signupForm.email)) e.email = "Invalid email";
    if (!signupForm.password) e.password = "Password is required";
    else if (signupForm.password.length < 6) e.password = "At least 6 characters";
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true); setErrors({});
    try {
      await api.post("/auth/signup", { name: signupForm.name.trim(), email: signupForm.email.trim().toLowerCase(), password: signupForm.password });
      const em = signupForm.email.trim().toLowerCase();
      setOtpEmail(em);
      toast.success("Check your email for the code!");
      setCardMode("otp");
    } catch (err) {
      const msg = err.response?.data?.message || "Signup failed.";
      toast.error(msg); setErrors({ general: msg });
    } finally { setLoading(false); }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const res = await api.post("/auth/google", { credential: credentialResponse.credential });
      loginWithToken(res.data.token, res.data.user);
      toast.success(`Welcome, ${res.data.user.name}!`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Google sign-in failed.");
    }
  };

  const handleOTPSuccess = ({ token, user }) => {
    loginWithToken(token, user);
    toast.success(`Welcome to Parsian, ${user.name}!`);
  };

  const word = HERO_WORDS[wordIdx];

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", background: "#f5f0e8", overflowX: "hidden" }}>
      <style>{`
        .lp-reveal { opacity: 0; transform: translateY(22px); transition: opacity .7s ease, transform .7s ease; }
        .lp-visible { opacity: 1; transform: translateY(0); }
        .lp-nav-link:hover { color: #111 !important; background: rgba(0,0,0,.04) !important; }
        .lp-ghost-btn:hover { border-color: #1a8f68 !important; color: #1a8f68 !important; }
        .lp-primary-btn:hover { background: #157a58 !important; transform: translateY(-1px); box-shadow: 0 4px 16px rgba(26,143,104,.25); }
        .lp-hero-primary:hover { background: #157a58 !important; transform: translateY(-2px); box-shadow: 0 8px 28px rgba(26,143,104,.3); }
        .lp-hero-ghost:hover { border-color: #1a8f68 !important; color: #1a8f68 !important; }
        .lp-lex-row:hover { background: #f0ebe2 !important; }
        .lp-cta-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(0,0,0,.2); }
        .lp-ft-link:hover { color: rgba(255,255,255,.75) !important; }
        .lp-field-input:focus { border-color: #1a8f68 !important; background: #fff !important; box-shadow: 0 0 0 3px rgba(26,143,104,.1) !important; }
        .lp-how-card::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: #1a8f68; border-radius: 3px 3px 0 0; }
        .lp-signin-btn:hover { background: #157a58 !important; }
        @media (max-width: 768px) {
          .lp-hero { grid-template-columns: 1fr !important; padding: 80px 0 40px !important; gap: 28px !important; }
          .lp-login-card { position: static !important; order: -1 !important; padding: 26px 20px !important; border-radius: 16px !important; }
          .lp-hero-left { order: 1 !important; }
          .lp-how-grid { grid-template-columns: 1fr !important; }
          .lp-2col { grid-template-columns: 1fr !important; }
          .lp-nav-links { display: none !important; }
          .lp-nav-get-started span { display: none !important; }
          .lp-stat-grid { grid-template-columns: 1fr 1fr !important; }
          section { padding: 56px 20px !important; }
          .lp-hero-stats { gap: 16px !important; flex-wrap: wrap !important; }
          .lp-hero-btns { flex-direction: column !important; }
          .lp-hero-btns button { width: 100% !important; justify-content: center !important; }
          .lp-hero-left h1 { font-size: 36px !important; letter-spacing: -1px !important; }
          .lp-hero-left p { font-size: 14px !important; }
        }
      `}</style>

      {/* NAV */}
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 200, height: 60, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", background: "rgba(245,240,232,0.88)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(0,0,0,.06)", boxShadow: navShadow ? "0 4px 24px rgba(0,0,0,.08)" : "none", transition: "box-shadow .3s" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer" }} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <TulipMark size={32} />
          <span style={{ fontSize: 17, fontWeight: 800, color: "#111", letterSpacing: -0.4 }}>Parsian</span>
        </div>
        <div className="lp-nav-links" style={{ display: "flex", gap: 4 }}>
          {[["how", "How it works"], ["missions", "Missions"], ["lexicon", "Lexicon"], ["stats", "Stats"]].map(([id, label]) => (
            <button key={id} onClick={() => scrollTo(id)} className="lp-nav-link" style={{ fontSize: 13, fontWeight: 600, color: "#555", background: "none", border: "none", padding: "7px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", transition: "all .15s" }}>{label}</button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => { switchToLogin(); setTimeout(() => cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50); }} className="lp-ghost-btn" style={{ background: "none", border: "1.5px solid #d5cfc5", borderRadius: 9, padding: "8px 18px", fontSize: 13, fontWeight: 600, color: "#555", cursor: "pointer", fontFamily: "inherit", transition: "all .15s" }}>Sign in</button>
          <button onClick={switchToSignup} className="lp-primary-btn" style={{ background: "#1a8f68", border: "none", borderRadius: 9, padding: "9px 20px", fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6, transition: "all .15s" }}>
            Get started free
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M2 7h10M7 2l5 5-5 5"/></svg>
          </button>
        </div>
      </nav>

      {/* HERO */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px" }}>
        <div className="lp-hero" style={{ minHeight: "100vh", padding: "110px 0 90px", display: "grid", gridTemplateColumns: "1fr 420px", gap: 80, alignItems: "center" }}>
          {/* Left */}
          <div className="lp-hero-left" style={{ position: "relative" }}>
            <div style={{ position: "absolute", right: -20, top: -60, fontFamily: "'Scheherazade New', serif", fontSize: 260, color: "rgba(26,143,104,.04)", fontWeight: 700, lineHeight: 1, userSelect: "none", pointerEvents: "none", direction: "rtl" }}>یاد</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#1a8f68", letterSpacing: 2, textTransform: "uppercase", marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
              Learn Persian the smart way
            </div>
            <h1 style={{ fontSize: "clamp(44px,5.5vw,68px)", fontWeight: 900, lineHeight: 1.02, letterSpacing: -2.5, color: "#111", marginBottom: 0 }}>
              <span style={{ display: "block" }}>The word is</span>
              <div style={{ display: "block", minHeight: 90, position: "relative", margin: "6px 0" }}>
                <span key={wordIdx} style={{ fontFamily: "Vazirmatn, 'Scheherazade New', serif", fontSize: "clamp(54px,7vw,84px)", fontWeight: 700, color: "#1a8f68", direction: "rtl", opacity: wordVisible ? 1 : 0, transform: wordVisible ? "translateY(0)" : "translateY(-14px)", transition: "opacity .55s ease, transform .55s ease", display: "block", whiteSpace: "nowrap", lineHeight: 1.1 }}>
                  {word.fa}
                </span>
              </div>
              <span style={{ display: "block", color: "#1a8f68" }}>learn it forever.</span>
            </h1>
            <p style={{ fontSize: 16, color: "#777", lineHeight: 1.75, maxWidth: 480, marginTop: 24, marginBottom: 32 }}>
              Parsian uses <strong style={{ color: "#111" }}>Half-Life Regression</strong> — memory science from researchers — to show you each Persian word exactly when you're about to forget it. Twice the retention, half the time.
            </p>
            <div className="lp-hero-stats" style={{ display: "flex", gap: 32, marginBottom: 36 }}>
              {[["200+", "Core words"], ["10", "Missions"], ["Free", "Forever"], ["2×", "Faster recall"]].map(([v, l]) => (
                <div key={l}><div style={{ fontSize: 26, fontWeight: 900, color: "#111", letterSpacing: -0.8 }}>{v}</div><div style={{ fontSize: 12, color: "#b0a898", marginTop: 1 }}>{l}</div></div>
              ))}
            </div>
            <div className="lp-hero-btns" style={{ display: "flex", gap: 12 }}>
              <button onClick={switchToSignup} className="lp-hero-primary" style={{ background: "#1a8f68", color: "#fff", border: "none", borderRadius: 12, padding: "15px 28px", fontSize: 15, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8, transition: "all .2s" }}>
                Start learning free
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
              </button>
              <button onClick={() => scrollTo("how")} className="lp-hero-ghost" style={{ background: "#fff", color: "#555", border: "1.5px solid #e8e2d8", borderRadius: 12, padding: "14px 24px", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all .2s" }}>
                See how it works
              </button>
            </div>
          </div>

          {/* Auth card */}
          <div id="login-form" ref={cardRef} className="lp-login-card" style={{ background: "#fff", borderRadius: 22, padding: "38px 34px", border: "1px solid #e8e2d8", boxShadow: "0 16px 64px rgba(0,0,0,.09), 0 4px 16px rgba(0,0,0,.05)", position: "sticky", top: 78 }}>
            {cardMode === "otp" ? (
              <OTPVerify email={otpEmail} type="signup" onSuccess={handleOTPSuccess} onBack={() => { setCardMode("signup"); setErrors({}); }} inline />
            ) : cardMode === "signup" ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 22 }}>
                  <TulipMark size={30} />
                  <span style={{ fontSize: 15, fontWeight: 800, color: "#111" }}>Parsian</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#111", letterSpacing: -0.5, marginBottom: 4 }}>Create your account</div>
                <div style={{ fontSize: 13, color: "#b0a898", marginBottom: 22 }}>Join thousands learning Persian for free</div>

                <LcField label="Full name" error={errors.name}>
                  <LcInput icon={<UserIcon />} type="text" placeholder="Muhammad Yahya" value={signupForm.name} onChange={e => setSignupForm(f => ({ ...f, name: e.target.value }))} onKeyDown={e => e.key === "Enter" && handleSignup()} error={errors.name} autoFocus />
                </LcField>
                <LcField label="Email" error={errors.email}>
                  <LcInput icon={<EmailIcon />} type="email" placeholder="you@example.com" value={signupForm.email} onChange={e => setSignupForm(f => ({ ...f, email: e.target.value }))} onKeyDown={e => e.key === "Enter" && handleSignup()} error={errors.email} />
                </LcField>
                <LcField label="Password" error={errors.password}>
                  <LcInput icon={<LockIcon />} type="password" placeholder="Min. 6 characters" value={signupForm.password} onChange={e => setSignupForm(f => ({ ...f, password: e.target.value }))} onKeyDown={e => e.key === "Enter" && handleSignup()} error={errors.password} />
                </LcField>

                {errors.general && <div style={{ padding: "9px 13px", borderRadius: 9, background: "#fef2f2", border: "1px solid #fca5a5", fontSize: 12, color: "#dc2626", marginBottom: 12 }}>{errors.general}</div>}

                <button onClick={handleSignup} disabled={loading} className="lp-signin-btn" style={{ width: "100%", padding: "14px", background: loading ? "#4ec99a" : "#1a8f68", color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background .15s", marginBottom: 0 }}>
                  {loading ? "Creating account…" : "Create free account"}
                  {!loading && <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>}
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0" }}>
                  <div style={{ flex: 1, height: 1, background: "#e8e2d8" }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#c0b8ae" }}>OR</span>
                  <div style={{ flex: 1, height: 1, background: "#e8e2d8" }} />
                </div>

                <div style={{ display: "flex", justifyContent: "center" }}>
                  <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => toast.error("Google sign-in failed.")} text="continue_with" shape="rectangular" size="large" width={isMobile ? "280" : "316"} />
                </div>

                <div style={{ textAlign: "center", fontSize: 13, color: "#b0a898", marginTop: 16 }}>
                  Already have an account?{" "}
                  <button onClick={switchToLogin} style={{ color: "#1a8f68", fontWeight: 700, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>Sign in</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 22 }}>
                  <TulipMark size={30} />
                  <span style={{ fontSize: 15, fontWeight: 800, color: "#111" }}>Parsian</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#111", letterSpacing: -0.5, marginBottom: 4 }}>Welcome back</div>
                <div style={{ fontSize: 13, color: "#b0a898", marginBottom: 22 }}>Sign in to continue your Persian journey</div>

                <LcField label="Email" error={errors.email}>
                  <LcInput icon={<EmailIcon />} type="email" placeholder="you@example.com" value={loginForm.email} onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))} onKeyDown={e => e.key === "Enter" && handleLogin()} error={errors.email} />
                </LcField>
                <LcField label="Password" error={errors.password}>
                  <LcInput icon={<LockIcon />} type="password" placeholder="••••••••" value={loginForm.password} onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))} onKeyDown={e => e.key === "Enter" && handleLogin()} error={errors.password} />
                </LcField>

                {errors.general && <div style={{ padding: "9px 13px", borderRadius: 9, background: "#fef2f2", border: "1px solid #fca5a5", fontSize: 12, color: "#dc2626", marginBottom: 12 }}>{errors.general}</div>}

                <button onClick={handleLogin} disabled={loading} className="lp-signin-btn" style={{ width: "100%", padding: "14px", background: loading ? "#4ec99a" : "#1a8f68", color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background .15s", marginBottom: 0 }}>
                  {loading ? "Signing in…" : "Sign in"}
                  {!loading && <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>}
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0" }}>
                  <div style={{ flex: 1, height: 1, background: "#e8e2d8" }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#c0b8ae" }}>OR</span>
                  <div style={{ flex: 1, height: 1, background: "#e8e2d8" }} />
                </div>

                <div style={{ display: "flex", justifyContent: "center" }}>
                  <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => toast.error("Google sign-in failed.")} text="continue_with" shape="rectangular" size="large" width={isMobile ? "280" : "316"} />
                </div>

                <div style={{ textAlign: "center", fontSize: 13, color: "#b0a898", marginTop: 16 }}>
                  New here?{" "}
                  <button onClick={switchToSignup} style={{ color: "#1a8f68", fontWeight: 700, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>Create free account</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section id="how" style={{ padding: "96px 48px", background: "#fff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="lp-reveal">
            <div style={eyebrowStyle}>How it works</div>
            <div style={titleStyle}>Three steps to fluent Persian</div>
            <p style={bodyStyle}>No cramming. No drills. Just smart review that works around your life.</p>
          </div>
          <div className="lp-how-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 22, marginTop: 56 }}>
            {[
              { n: "01", title: "Pick a Mission", body: "Each mission unlocks 20 words around a theme — greetings, family, the bazaar. Complete them in order for real depth.", word: "ماموریت" },
              { n: "02", title: "Flip the Flashcard", body: "See the Persian word, pick its meaning from four options. The card flips to show the answer plus an example sentence.", word: "کارت" },
              { n: "03", title: "HLR Does the Rest", body: "Our Half-Life Regression model tracks how long each word stays in memory and brings it back at the perfect moment.", word: "حافظه" },
            ].map((c, i) => (
              <div key={i} className="lp-reveal lp-how-card" style={{ background: "#fff", borderRadius: 20, padding: "30px 26px", border: "1px solid #ede8df", position: "relative", overflow: "hidden", transitionDelay: `${i * 0.1}s` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#1a8f68", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 14 }}>Step {c.n}</div>
                <div style={{ width: 50, height: 50, borderRadius: 14, background: "#e8f7f1", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>
                  {i === 0 && <svg width="24" height="24" viewBox="0 0 22 26" fill="none"><line x1="11" y1="25" x2="11" y2="16" stroke="#1a8f68" strokeWidth="1.6" strokeLinecap="round"/><path d="M11 20 Q7 18 6.5 14 Q9 15.5 11 19" fill="#1a8f68" opacity="0.5"/><path d="M11 16 Q6 12 6 7.5 Q8.5 5.5 11 9 Q13.5 5.5 16 7.5 Q16 12 11 16Z" fill="#1a8f68"/></svg>}
                  {i === 1 && <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1a8f68" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="3"/><path d="M8 10l4 4 4-4"/></svg>}
                  {i === 2 && <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1a8f68" strokeWidth="1.8" strokeLinecap="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>}
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: "#111", letterSpacing: -0.4, marginBottom: 8 }}>{c.title}</h3>
                <p style={{ fontSize: 14, color: "#888", lineHeight: 1.65 }}>{c.body}</p>
                <div style={{ fontFamily: "Vazirmatn, 'Scheherazade New', serif", fontSize: 34, fontWeight: 700, color: "#1a8f68", direction: "rtl", marginTop: 14 }}>{c.word}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSIONS */}
      <section id="missions" style={{ padding: "96px 48px", background: "#111" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "center" }} className="lp-2col">
            <div>
              <div className="lp-reveal">
                <div style={{ ...eyebrowStyle, color: "#4ec99a" }}>Missions</div>
                <div style={{ ...titleStyle, color: "#fff" }}>Every word has its place.</div>
                <p style={{ ...bodyStyle, color: "rgba(255,255,255,.5)" }}>Ten missions take you from your first سلام to confident everyday conversation. Each unlocks when you're ready.</p>
              </div>
              <div className="lp-reveal" style={{ marginTop: 36, transitionDelay: ".1s" }}>
                {[
                  { title: "Mission 0 — The Basics", sub: "Numbers, colors, core nouns", badge: "done" },
                  { title: "Mission 1 — Greetings", sub: "Everyday hellos & goodbyes", badge: "active" },
                  { title: "Mission 2 — The Family", sub: "Family members & relationships", badge: "locked" },
                  { title: "Mission 3 — The Bazaar", sub: "Shopping & marketplace words", badge: "locked" },
                ].map((m, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 0", borderBottom: "1px solid rgba(255,255,255,.07)", borderTop: i === 0 ? "1px solid rgba(255,255,255,.07)" : "none" }}>
                    <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,.07)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {i === 0 && <svg width="18" height="18" viewBox="0 0 22 26" fill="none"><line x1="11" y1="25" x2="11" y2="16" stroke="rgba(255,255,255,.6)" strokeWidth="1.6" strokeLinecap="round"/><path d="M11 16 Q6 12 6 7.5 Q8.5 5.5 11 9 Q13.5 5.5 16 7.5 Q16 12 11 16Z" fill="rgba(255,255,255,.7)"/></svg>}
                      {i === 1 && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.6)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11V6a1 1 0 012 0v5M11 6V4a1 1 0 012 0v7M13 6a1 1 0 012 0v5M7 12V9a1 1 0 012 0v3"/><path d="M7 13c0 3.3 1.8 6 5 7 3 .9 5-1 5-4v-3"/></svg>}
                      {i > 1 && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.25)" strokeWidth="1.8" strokeLinecap="round"><rect x="6" y="11" width="12" height="9" rx="2"/><path d="M9 11V7a3 3 0 016 0v4"/></svg>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 1 }}>{m.title}</div>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,.38)" }}>{m.sub}</div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, letterSpacing: .5, textTransform: "uppercase", flexShrink: 0, background: m.badge === "done" ? "rgba(26,143,104,.2)" : m.badge === "active" ? "#1a8f68" : "rgba(255,255,255,.07)", color: m.badge === "done" ? "#4ec99a" : m.badge === "active" ? "#fff" : "rgba(255,255,255,.28)" }}>
                      {m.badge === "done" ? "Done" : m.badge === "active" ? "Active" : "Locked"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lp-reveal" style={{ transitionDelay: ".15s" }}>
              <div style={{ background: "rgba(255,255,255,.05)", borderRadius: 22, padding: 36, border: "1px solid rgba(255,255,255,.08)", textAlign: "center" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,.38)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 14 }}>Translate this word</div>
                <div ref={scWordRef} style={{ fontFamily: "Vazirmatn, 'Scheherazade New', serif", fontSize: 88, fontWeight: 700, color: "#fff", direction: "rtl", lineHeight: 1, marginBottom: 10, transition: "opacity .4s,transform .4s" }}>{HERO_WORDS[0].fa}</div>
                <div ref={scTransRef} style={{ fontSize: 18, fontWeight: 700, color: "#4ec99a", marginBottom: 22, transition: "opacity .4s" }}>{HERO_WORDS[0].en}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div style={{ padding: 11, borderRadius: 10, fontSize: 13, fontWeight: 600, textAlign: "center", background: "rgba(26,143,104,.2)", color: "#4ec99a", border: "2px solid #1a8f68" }}>✓ Book</div>
                  {["Water", "House", "Day"].map(w => <div key={w} style={{ padding: 11, borderRadius: 10, fontSize: 13, fontWeight: 600, textAlign: "center", background: "rgba(255,255,255,.04)", color: "rgba(255,255,255,.28)", border: "2px solid rgba(255,255,255,.08)" }}>{w}</div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEXICON */}
      <section id="lexicon" style={{ padding: "96px 48px", background: "#fff8f0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 72, alignItems: "center" }} className="lp-2col">
            <div className="lp-reveal">
              <div style={eyebrowStyle}>Lexicon</div>
              <div style={titleStyle}>Every word you've ever learned,<br/><span style={{ color: "#1a8f68" }}>always with you.</span></div>
              <p style={{ ...bodyStyle, marginTop: 16 }}>Unlocked words collect in your personal Lexicon. Search, filter by memory strength, and jump straight into practice for any word that needs attention.</p>
              <div style={{ display: "flex", gap: 10, marginTop: 28, flexWrap: "wrap" }}>
                {[["All (14)", true], ["Strong (6)", false], ["Need review (8)", false]].map(([l, active]) => (
                  <div key={l} style={{ background: active ? "#1a8f68" : "#fff", border: `1.5px solid ${active ? "#1a8f68" : "#e8e2d8"}`, color: active ? "#fff" : "#888", fontSize: 12, fontWeight: active ? 700 : 600, padding: "7px 16px", borderRadius: 20 }}>{l}</div>
                ))}
              </div>
            </div>
            <div className="lp-reveal" style={{ transitionDelay: ".12s" }}>
              <div style={{ background: "#fff", borderRadius: 20, padding: 22, border: "1px solid #ede8df", boxShadow: "0 8px 32px rgba(0,0,0,.07)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 800, color: "#111" }}>Lexicon</h3>
                  <span style={{ fontSize: 12, color: "#b0a898" }}>14 words learned</span>
                </div>
                {[{ fa: "سلام", en: "Hello", c: "#1a8f68" }, { fa: "کتاب", en: "Book", c: "#1a8f68" }, { fa: "خانه", en: "House", c: "#f0a020" }, { fa: "دوست", en: "Friend", c: "#f0a020" }, { fa: "مادر", en: "Mother", c: "#e24b4a" }].map((w, i) => (
                  <div key={i} className="lp-lex-row" style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 13px", background: "#faf8f5", borderRadius: 11, marginBottom: 7, cursor: "pointer", transition: "background .15s" }}>
                    <div style={{ width: 24, height: 24, borderRadius: 7, background: "#fff", border: "1px solid #ede8df", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#b0a898", flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ fontFamily: "Vazirmatn, 'Scheherazade New', serif", fontSize: 19, fontWeight: 700, direction: "rtl", color: "#111", minWidth: 68 }}>{w.fa}</div>
                    <div style={{ flex: 1, fontSize: 13, color: "#555" }}>{w.en}</div>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: w.c, flexShrink: 0 }} />
                  </div>
                ))}
                <div style={{ textAlign: "center", padding: "10px 0", fontSize: 12, color: "#b0a898", fontWeight: 600 }}>+ 9 more words →</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section id="stats" style={{ padding: "96px 48px", background: "#f5f0e8" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 72, alignItems: "center" }} className="lp-2col">
            <div className="lp-reveal">
              <div style={{ background: "linear-gradient(135deg,#1a8f68,#157a58)", borderRadius: 16, padding: 26, marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.6)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Your progress</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: "#fff", letterSpacing: -1 }}>84%</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 4 }}>Average accuracy across all sessions</div>
              </div>
              <div className="lp-stat-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {[{ bg: "#fff5e0", ic: "#e8900a", v: 67, l: "Words seen" }, { bg: "#f0eeff", ic: "#7c5cdb", v: 42, l: "Strong words" }, { bg: "#e8f7f1", ic: "#1a8f68", v: 312, l: "Total reviews" }, { bg: "#fff5e0", ic: "#e8900a", v: "🔥 7", l: "Day streak" }].map((s, i) => (
                  <div key={i} style={{ background: "#fff", borderRadius: 15, padding: "18px 20px", border: "1px solid #ede8df" }}>
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10, color: s.ic }}>
                      {i === 3 ? <span style={{ fontSize: 16 }}>🔥</span> : <svg width="17" height="17" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">{i === 0 ? <><path d="M3 3h9a1 1 0 011 1v10a1 1 0 01-1 1H3V3z"/><path d="M12 6h2a1 1 0 011 1v6a1 1 0 01-1 1h-2"/></> : i === 1 ? <path d="M10 1L3 11h5l-1 6 7-10h-5l1-6z"/> : <><circle cx="9" cy="9" r="7"/><circle cx="9" cy="9" r="3"/></>}</svg>}
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "#111", letterSpacing: -0.6 }}>{s.v}</div>
                    <div style={{ fontSize: 11, color: "#b0a898", marginTop: 1 }}>{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lp-reveal" style={{ transitionDelay: ".12s" }}>
              <div style={eyebrowStyle}>Stats</div>
              <div style={titleStyle}>Know exactly where you stand.</div>
              <p style={{ ...bodyStyle, marginTop: 16 }}>Your Stats tab shows which words are locked in memory, which need attention, and how your accuracy has grown over time.</p>
              <div style={{ background: "#fff", borderRadius: 15, padding: 20, border: "1px solid #ede8df", marginTop: 28 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 12 }}>Memory breakdown</div>
                {[{ l: "Strong", v: 42, w: 42, c: "#1a8f68" }, { l: "At risk", v: 18, w: 18, c: "#f0a020" }, { l: "Needs practice", v: 7, w: 7, c: "#e24b4a" }, { l: "Not started", v: 133, w: 66, c: "#d5cfc5" }].map((r, i) => (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 12 }}>
                      <span style={{ fontWeight: 600, color: "#555" }}>{r.l}</span>
                      <span style={{ fontWeight: 800, color: "#111" }}>{r.v}</span>
                    </div>
                    <div style={{ background: "#ede8df", borderRadius: 99, height: 7, overflow: "hidden" }}>
                      <div style={{ width: `${r.w}%`, height: "100%", borderRadius: 99, background: r.c }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div style={{ background: "#1a8f68", color: "#fff", padding: "100px 48px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", fontFamily: "'Scheherazade New', serif", fontSize: 280, color: "rgba(255,255,255,.05)", fontWeight: 700, pointerEvents: "none", whiteSpace: "nowrap", direction: "rtl" }}>زبان</div>
        <div style={{ position: "relative", maxWidth: 1100, margin: "0 auto" }} className="lp-reveal">
          <div style={{ ...eyebrowStyle, color: "rgba(255,255,255,.55)", justifyContent: "center", display: "flex" }}>Get started</div>
          <div style={{ fontSize: "clamp(30px,4vw,46px)", fontWeight: 900, color: "#fff", lineHeight: 1.05, letterSpacing: -1.5, maxWidth: 560, margin: "0 auto 16px" }}>Your first word is waiting.</div>
          <p style={{ fontSize: 16, color: "rgba(255,255,255,.68)", maxWidth: 420, margin: "0 auto 32px", lineHeight: 1.7 }}>Free, beautiful, and science-backed. Join thousands already learning Persian the smart way.</p>
          <button onClick={switchToSignup} className="lp-cta-btn" style={{ background: "#fff", color: "#1a8f68", border: "none", borderRadius: 12, padding: "16px 36px", fontSize: 16, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 8, transition: "all .2s" }}>
            Start learning for free
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#1a8f68" strokeWidth="2.5" strokeLinecap="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
          </button>
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{ background: "#111", color: "rgba(255,255,255,.38)", padding: "44px 48px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <TulipMark size={28} />
          <span style={{ color: "rgba(255,255,255,.45)", fontSize: 14, fontWeight: 700, marginLeft: 4 }}>Parsian</span>
        </div>
        <div style={{ display: "flex", gap: 22, fontSize: 13 }}>
          {["How it works", "Privacy", "Terms", "Contact"].map(l => (
            <a key={l} href="#" className="lp-ft-link" style={{ color: "rgba(255,255,255,.38)", textDecoration: "none", transition: "color .15s" }}>{l}</a>
          ))}
        </div>
        <div style={{ fontSize: 12 }}>© 2025 Parsian. Free forever.</div>
      </footer>
    </div>
  );
}

function LcField({ label, error, children }) {
  return (
    <div style={{ marginBottom: 13 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#888", letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 5 }}>{label}</label>
      {children}
      {error && <div style={{ fontSize: 11, color: "#e24b4a", marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function LcInput({ icon, error, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: focused ? "#1a8f68" : "#c0b8ae", pointerEvents: "none" }}>{icon}</div>
      <input {...props} className="lp-field-input" onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ width: "100%", padding: "13px 12px 13px 38px", border: `1.5px solid ${error ? "#e24b4a" : focused ? "#1a8f68" : "#e8e2d8"}`, borderRadius: 11, fontSize: 14, color: "#111", background: focused ? "#fff" : "#faf8f5", outline: "none", fontFamily: "inherit", boxSizing: "border-box", transition: "all .15s" }}
      />
    </div>
  );
}

function EmailIcon() { return <svg width="16" height="16" viewBox="0 0 17 17" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><rect x="1.5" y="3.5" width="14" height="10" rx="2"/><path d="M1.5 6l7 4.5L15.5 6"/></svg>; }
function LockIcon() { return <svg width="16" height="16" viewBox="0 0 17 17" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><rect x="3" y="7.5" width="11" height="8" rx="2"/><path d="M5.5 7.5V5a3 3 0 016 0v2.5"/></svg>; }
function UserIcon() { return <svg width="16" height="16" viewBox="0 0 17 17" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="8.5" cy="5.5" r="3"/><path d="M2 14.5c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5"/></svg>; }

const eyebrowStyle = { fontSize: 11, fontWeight: 700, color: "#1a8f68", letterSpacing: 2, textTransform: "uppercase", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 };
const titleStyle = { fontSize: "clamp(30px,4vw,46px)", fontWeight: 900, color: "#111", lineHeight: 1.05, letterSpacing: -1.5, marginBottom: 14 };
const bodyStyle = { fontSize: 16, color: "#777", lineHeight: 1.75, maxWidth: 500 };
